import { motion } from "framer-motion";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FileText,
  Zap,
  Shield,
  Clock,
  Brain,
  CheckCircle,
  ArrowRight,
  Upload,
  BarChart3,
  ExternalLink,
} from "lucide-react";
import aiEdgeExpertLogo from "@assets/ai-edge-expert_1758918621078.png";

const features = [
  {
    icon: <Brain className="h-8 w-8" />,
    title: "AI-Powered Analysis",
    description:
      "Advanced AI technology extracts key dates with exceptional accuracy from complex lease documents.",
  },
  {
    icon: <Zap className="h-8 w-8" />,
    title: "Lightning Fast",
    description:
      "Process multiple documents simultaneously with real-time progress tracking, all at the speed of AI.",
  },
  {
    icon: <Shield className="h-8 w-8" />,
    title: "Secure & Private",
    description:
      "Documents processed in memory only. No permanent storage. Your data stays private and secure.",
  },
  {
    icon: <Clock className="h-8 w-8" />,
    title: "Real-Time Progress",
    description:
      "Watch your documents being processed with live updates, time estimates, and detailed progress tracking.",
  },
];

const steps = [
  {
    step: "01",
    title: "Upload Documents",
    description:
      "Drag and drop your lease PDFs or select them from your device. Supports multiple files up to 20MB each.",
    icon: <Upload className="h-6 w-6" />,
  },
  {
    step: "02",
    title: "AI Processing",
    description:
      "Advanced AI analyzes your documents, identifying and extracting all relevant lease dates with context.",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    step: "03",
    title: "Get Results",
    description:
      "Receive comprehensive date extraction with names, descriptions, values, and precise document locations.",
    icon: <BarChart3 className="h-6 w-6" />,
  },
];

export default function HomePage() {
  const [, setLocation] = useLocation();
  const [showModal, setShowModal] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        {/* Upper-left - Other Apps Link */}
        <div className="absolute top-4 left-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open('https://adventuresincre.app', '_blank')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            data-testid="button-other-apps"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Other Apps by A.CRE
          </Button>
        </div>

       {/* Upper-right disclaimer and link */}
        <div className="absolute top-4 right-4 text-right space-y-2 z-10">
          <div className="text-xs text-gray-500 dark:text-gray-400 italic max-w-xs">
            For educational purposes only. 
            May produce inaccurate results. 
            Use at your own risk. 
            [beta v2.1]
          </div>
        </div>

        <div className="relative container mx-auto px-4 py-20 lg:py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium">
                <Zap className="w-4 h-4 mr-2" />
                AI-Powered Document Analysis
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-5xl lg:text-7xl font-bold gradient-text mb-6"
            >
              Extract Lease Dates
              <br />
              <span className="text-gray-900 dark:text-white">Instantly</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Transform your commercial real estate document processing with AI.
              Extract key dates from lease documents with precision and speed.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                size="xl"
                onClick={() => setLocation("/upload")}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                data-testid="button-get-started"
              >
                <span className="relative z-10 flex items-center">
                  Start Extracting Dates (Free)
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Button>
              <Button
                variant="outline"
                size="xl"
                onClick={() => setShowModal(true)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300 px-8 py-4 rounded-lg font-semibold transition-all duration-300"
                data-testid="button-whats-catch"
              >
                What's the Catch?
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Why Choose Our Platform?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
            >
              Built for commercial real estate professionals who need accurate,
              fast, and secure document processing.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
                  <CardHeader className="text-center pb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 mx-auto mb-4">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-xl font-semibold">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center text-gray-600 dark:text-gray-300 leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              How It Works
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
            >
              Simple, fast, and accurate lease date extraction in three easy
              steps.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="max-w-4xl mx-auto"
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex items-center mb-12 last:mb-0"
              >
                <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-xl mr-8">
                  {step.step}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center mb-2">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 mr-4">
                      {step.icon}
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed ml-14">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-white mb-4"
            >
              Ready to Transform Your Workflow?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto"
            >
              Offered as an educational tool. Thanks to the AI.Edge Team.
            </motion.p>
            <motion.div variants={itemVariants}>
              <Button
                size="xl"
                onClick={() => setLocation("/upload")}
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                data-testid="button-start-extracting"
              >
                <CheckCircle className="mr-2 h-5 w-5" />
                Start Extracting Dates (Free)
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's the Catch Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              What's the Catch?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-6 text-gray-600 dark:text-gray-300">
                <p className="text-lg leading-relaxed">
                  No catch, other than this is an educational app built by members of the AI.Edge by A.CRE. AI.Edge is the real estate industry's premier community of AI-Native professionals. Built by the team at A.CRE that delivers the technical financial modeling and AI skills needed to succeed in CRE.
                </p>
                
                <p className="text-lg leading-relaxed">
                  Want to give back? Hire an AI.Edge member! Or join the AI.Edge for free and learn to leverage AI for real estate.
                </p>
                
                <div className="flex justify-center">
                  <Button
                    onClick={() => window.open('https://aiedge.ac/join', '_blank')}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                    data-testid="button-learn-more-aiedge"
                  >
                    Learn More about AI.Edge
                  </Button>
                </div>
                
                <div className="border-t pt-4 mt-6">
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    This app is built by non-technical real estate professionals, leveraging AI tools. While the creators of this app strive for it to be accurate and effective, it is largely an educational app and may contain errors.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
